<template>
  <div class="admin-container">
    <div class="admin-header">
      <h2>Booking reply inbox</h2>
      <div class="admin-nav">
        <router-link to="/admin" class="nav-button">📅 Events</router-link>
        <router-link to="/admin_comics" class="nav-button">🎭 Comics</router-link>
        <router-link to="/admin_dates" class="nav-button">📊 Events</router-link>
        <router-link to="/admin_rota" class="nav-button">📋 Rota</router-link>
        <button class="logout-button" @click="logout">🚪 Logout</button>
      </div>
    </div>

    <section class="inbox-section">
      <p class="filter-hint">
        Showing rows where subject matches <code>{{ subjectPatternDisplay }}</code> (case-insensitive).
        <label class="toggle-all sheet-sync-pref">
          <input v-model="updateSpreadsheet" type="checkbox" @change="onUpdateSpreadsheetPrefChange" />
          Update spreadsheet on offer / confirm / reject (Sunset & Craic Acts)
        </label>
        <label class="toggle-all">
          <input v-model="showAllSubjects" type="checkbox" @change="loadRows" />
          Show all subjects (no filter)
        </label>
        <span class="batch-rerun-wrap">
          <button
            type="button"
            class="rerun-llm-batch-btn"
            :disabled="rerunBatchBusy || rows.length === 0"
            @click="rerunLlmMissingDebugBatch"
          >
            Re-run LLM (rows missing prompt/raw, up to 20)
          </button>
          <span class="batch-rerun-hint">
            Sends {{ rerunLlmChunkSize }} ids per Edge request (avoids timeouts / 502).
          </span>
        </span>
      </p>
      <p v-if="rerunError" class="rerun-error-banner">{{ rerunError }}</p>

      <details class="calibration-panel" @toggle="onCalibrationDetailsToggle">
        <summary class="calibration-summary">Calibration (tagged rows)</summary>
        <div v-if="calibrationError" class="calibration-error">{{ calibrationError }}</div>
        <div v-else-if="calibrationLoading" class="calibration-loading">Loading calibration…</div>
        <div v-else class="calibration-body">
          <p class="calibration-summary-line">{{ calibrationSummaryLine }}</p>
          <p v-if="calibrationBucketHint" class="calibration-hint">{{ calibrationBucketHint }}</p>
          <table class="calibration-table">
            <thead>
              <tr>
                <th>Confidence bucket</th>
                <th v-for="col in calibrationIntentColumns" :key="col">{{ col }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="br in calibrationTableRows" :key="br.bucketIndex">
                <td>{{ br.label }}</td>
                <td v-for="col in calibrationIntentColumns" :key="col + '-' + br.bucketIndex">
                  <template v-if="br.byIntent[col].n === 0">—</template>
                  <template v-else>
                    {{ br.byIntent[col].agree }}/{{ br.byIntent[col].n }}
                    ({{
                      Math.round((1000 * br.byIntent[col].agree) / br.byIntent[col].n) / 10
                    }}%)
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
          <p class="calibration-footnote">
            Auto-confirm runs only when stored confidence is at least 90% (constant
            <code>AUTO_CONFIRM_THRESHOLD</code> in <code>inboxConfirmFlow.js</code>, mirrored on the
            webhook Edge path). Each successful auto-confirm emails
            <strong>tansaku@gmail.com</strong> via Edge
            <code>inbox-auto-confirm-notify</code> (deploy it; needs <code>BREVO_API_KEY</code> like
            <code>send-application-email</code>).
          </p>
        </div>
      </details>

      <div v-if="isLoading" class="loading">Loading…</div>
      <div v-else-if="errorMessage" class="error-banner">{{ errorMessage }}</div>
      <div v-else-if="rows.length === 0" class="empty-state">
        No matching emails in <code>email_inbox_log</code> yet.
      </div>

      <ul v-else class="inbox-list">
        <li v-for="row in rows" :key="row.id" class="inbox-card">
          <header class="card-meta">
            <span class="meta-time">{{ formatWhen(row) }}</span>
            <span class="meta-subject" :title="row.subject">{{ row.subject }}</span>
            <span class="meta-sender">{{ row.sender_email }}</span>
            <span v-if="truthDisagreesWithLlm(row)" class="truth-mismatch-chip">Truth ≠ LLM</span>
            <span v-if="row.auto_confirmed_at" class="auto-confirmed-badge">Auto-confirmed</span>
          </header>
          <div class="card-body-grid">
            <div class="body-panel">
              <div class="panel-label">Reply body</div>
              <pre class="body-text body-preview">{{ bodyPreview(row.body_snippet) }}</pre>
              <details v-if="bodyHasMoreLines(row.body_snippet)" class="body-details">
                <summary>Full body (includes quoted thread)</summary>
                <pre class="body-text body-full">{{ row.body_snippet || '—' }}</pre>
              </details>
            </div>
            <aside class="classification-panel">
              <div
                class="classify-summary-grid"
                :class="{ 'classify-summary-grid--single': !row.llm_summary }"
              >
                <div class="classify-column">
                  <div class="panel-label">Classification</div>
                  <div class="intent-row">
                    <span class="intent-badge" :class="intentClass(row.parsed_intent)">
                      {{ row.parsed_intent || '—' }}
                    </span>
                    <span v-if="row.confidence != null" class="confidence">
                      {{ formatConfidence(row.confidence) }}
                    </span>
                  </div>
                  <div v-if="sourceLabel(row)" class="source-row">
                    <span class="source-badge">{{ sourceLabel(row) }}</span>
                  </div>
                  <div class="truth-row">
                    <span class="truth-label">Truth:</span>
                    <select
                      class="truth-select"
                      :value="row.ground_truth_intent || ''"
                      :disabled="groundTruthBusyRowId === row.id"
                      @change="onGroundTruthIntentChange(row, $event)"
                    >
                      <option value="">(unset)</option>
                      <option v-for="intent in INBOX_GROUND_TRUTH_INTENTS" :key="intent" :value="intent">
                        {{ intent }}
                      </option>
                    </select>
                    <span v-if="row.ground_truth_intent" class="truth-compare">
                      {{ truthMatchesLlm(row) ? '✓ matches LLM' : '✗ disagrees' }}
                    </span>
                  </div>
                  <div v-if="row.auto_confirmed_at" class="undo-auto-row">
                    <button
                      type="button"
                      class="undo-auto-btn"
                      :disabled="isUndoAutoBusy(row.id)"
                      @click="undoAutoConfirmForRow(row)"
                    >
                      {{ isUndoAutoBusy(row.id) ? '…' : 'Undo auto-confirm' }}
                    </button>
                  </div>
                  <div class="rerun-row-actions">
                    <button
                      type="button"
                      class="rerun-llm-btn"
                      :disabled="rerunBusyId === row.id"
                      @click="rerunLlmSingle(row)"
                    >
                      {{ rerunBusyId === row.id ? 'Re-running…' : 'Re-run LLM' }}
                    </button>
                    <button
                      v-if="hasLlmDebug(row)"
                      type="button"
                      class="llm-debug-btn"
                      @click="openLlmModal(row)"
                    >
                      LLM prompt &amp; raw
                    </button>
                  </div>
                </div>
                <div v-if="row.llm_summary" class="summary-column">
                  <div class="panel-label subtle">LLM summary</div>
                  <p class="llm-summary">{{ row.llm_summary }}</p>
                </div>
              </div>
              <div v-if="formatEffectiveOfferDate(row) || formatSubjectOfferDate(row)" class="offer-date-block">
                <div class="panel-label subtle">Date for matching</div>
                <p v-if="formatEffectiveOfferDate(row)" class="offer-date-value">
                  {{ formatEffectiveOfferDate(row) }}
                  <span v-if="row.llm_inferred_event_date" class="offer-date-source"> (LLM inferred)</span>
                </p>
                <p v-if="subjectOfferDiffersFromEffective(row)" class="offer-date-subject-hint">
                  Subject offer line: {{ formatSubjectOfferDate(row) }}
                </p>
              </div>
              <div v-if="row.application_ids?.length" class="ids-block">
                <span class="panel-label subtle">LLM application IDs</span>
                <span class="app-ids">{{ row.application_ids.join(', ') }}</span>
              </div>
              <div class="inbox-apps-panel">
                <div class="panel-label subtle">Applications (human confirm / reject)</div>
                <p v-if="candidatesError" class="candidates-error">{{ candidatesError }}</p>
                <p
                  v-else-if="!inboxCandidatesByRowId[row.id]?.length"
                  class="candidates-empty"
                >
                  <template v-if="offerDateYmd(row)">
                    No application for this sender on
                    <strong>{{ formatParsedOfferDate(row) }}</strong>. Check Events &amp; Applications
                    or re-run LLM.
                  </template>
                  <template v-else>
                    No applications loaded for this sender and we could not parse an offer date from
                    the subject. Re-run LLM or check Events &amp; Applications.
                  </template>
                </p>
                <ul v-else class="inbox-app-list">
                  <li
                    v-for="cand in inboxCandidatesByRowId[row.id]"
                    :key="cand.id"
                    class="inbox-app-row"
                    :class="{
                      'row-date-match': cand.matchesEmailOfferDate,
                      'row-llm-only': cand.suggestedByLlm && !cand.matchesEmailOfferDate,
                    }"
                  >
                    <div class="inbox-app-primary">
                      <strong class="inbox-app-name">{{ cand.comedian_name || '—' }}</strong>
                      <div class="inbox-app-event">{{ formatInboxEventLine(cand) }}</div>
                    </div>
                    <div
                      class="inbox-app-badges-actions"
                      :class="{ 'inbox-app-badges-actions--terminal': isTerminalInboxStatus(cand.status) }"
                    >
                      <div class="inbox-candidate-badges">
                        <span
                          class="status-chip"
                          :style="{
                            borderColor: getStatusColor(cand.status),
                            color: getStatusColor(cand.status),
                          }"
                        >
                          {{ getStatusLabel(cand.status) }}
                        </span>
                        <span v-if="cand.matchesEmailOfferDate" class="date-match-badge"
                          >Matches offer date</span
                        >
                        <span v-if="cand.suggestedByLlm" class="llm-suggest-badge">LLM pick</span>
                        <span
                          v-if="
                            offerDateYmd(row) && cand.suggestedByLlm && !cand.matchesEmailOfferDate
                          "
                          class="date-mismatch-badge"
                          >LLM pick ≠ offer date</span
                        >
                      </div>
                      <div v-if="isTerminalInboxStatus(cand.status)" class="done-hint">
                        {{ getStatusLabel(cand.status) }}.
                      </div>
                      <div v-else class="inbox-app-actions">
                        <button
                          type="button"
                          class="inbox-confirm-btn"
                          :disabled="
                            !canConfirmInboxApplication(cand) ||
                            isInboxActionBusy(row.id, cand.id)
                          "
                          @click="confirmInboxApplication(row, cand)"
                        >
                          {{
                            isInboxActionBusy(row.id, cand.id)
                              ? '…'
                              : 'Confirm'
                          }}
                        </button>
                        <button
                          type="button"
                          class="inbox-reject-btn"
                          :disabled="
                            !canRejectInboxApplication(cand) ||
                            isInboxActionBusy(row.id, cand.id)
                          "
                          @click="rejectInboxApplication(row, cand)"
                        >
                          {{
                            isInboxActionBusy(row.id, cand.id)
                              ? '…'
                              : 'Reject'
                          }}
                        </button>
                      </div>
                    </div>
                    <p
                      v-if="
                        !canConfirmInboxApplication(cand) &&
                        canRejectInboxApplication(cand)
                      "
                      class="cand-warn"
                      title="Confirm only when status is Offered; Reject is allowed for this offer date when Pending."
                    >
                      Confirm needs <strong>Offered</strong>. Reject OK here.
                      <router-link to="/admin_dates" class="cand-warn-link">Events</router-link>
                    </p>
                    <p
                      v-else-if="!canConfirmInboxApplication(cand) && !canRejectInboxApplication(cand)"
                      class="cand-warn"
                      title="Use Events & Applications to change status, or pick a row that matches the offer date."
                    >
                      No actions here —
                      <router-link to="/admin_dates" class="cand-warn-link">Events &amp; Applications</router-link>.
                    </p>
                  </li>
                </ul>
              </div>
            </aside>
          </div>
        </li>
      </ul>

      <div
        v-if="llmModalRow"
        class="modal-backdrop"
        role="dialog"
        aria-modal="true"
        aria-labelledby="llm-modal-title"
        @click.self="closeLlmModal"
      >
        <div class="modal-dialog">
          <div class="modal-header">
            <h3 id="llm-modal-title">LLM debug</h3>
            <button type="button" class="modal-close" aria-label="Close" @click="closeLlmModal">
              ×
            </button>
          </div>
          <div class="modal-body-scroll">
            <div class="modal-block">
              <div class="modal-block-title">Prompt sent</div>
              <pre class="modal-pre">{{ llmModalRow.llm_prompt || '—' }}</pre>
            </div>
            <div class="modal-block">
              <div class="modal-block-title">Raw model response</div>
              <pre class="modal-pre">{{ llmModalRow.llm_raw_response || '—' }}</pre>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { supabase } from '../supabaseClient';
import { logoutAdmin } from '../utils/auth.js';
import {
  BOOKING_REPLY_SUBJECT_ILIKE,
  parseOfferEventDateFromSubject,
  resolveOfferDateForInboxRow,
} from '../utils/bookingEmail.js';
import {
  buildInboxCandidatesForRow as buildInboxCandidatesMapForRow,
  INBOX_APP_SELECT,
  normalizeInboxSender,
} from '../utils/inboxCandidates.js';
import {
  buildCalibrationTableRows,
  calibrationOverallAgreement,
  findHighestReliableBucketForIntent,
  CALIBRATION_MIN_ACCURACY,
  CALIBRATION_MIN_BUCKET_SAMPLE,
} from '../utils/inboxCalibration.js';
import { notifyInboxAutoConfirmByEmail } from '../utils/inboxAutoConfirmNotify.js';
import {
  formatSheetSyncResultNote,
  readInboxUpdateSpreadsheetPref,
  saveInboxUpdateSpreadsheetPref,
  syncLineupSheetFromInbox,
} from '../utils/sheetLineupSync.js';
import {
  attemptAutoConfirm,
  confirmApplicationFromInboxCandidate,
  undoAutoConfirmInbox,
} from '../utils/inboxConfirmFlow.js';
import {
  INBOX_GROUND_TRUTH_INTENTS,
  setInboxRowGroundTruth,
  setInboxRowGroundTruthIfUnset,
} from '../utils/inboxGroundTruth.js';
import {
  APPLICATION_STATUSES,
  getStatusLabel,
  getStatusColor,
} from '../utils/applicationStatus.js';
import { sortInboxRowsReverseChronological } from '../utils/inboxRowsSort.js';
import Swal from 'sweetalert2';

/** @see docs/APP_UI_SPEC.md — D1, D2, D3, D4 */

/** Must match `MAX_IDS_PER_REQUEST` in `supabase/functions/email-inbox-rerun-llm/index.ts`. */
const RERUN_LLM_CHUNK_SIZE = 2;

export default {
  name: 'AdminInboxReviewPage',
  data() {
    return {
      APPLICATION_STATUSES,
      rerunLlmChunkSize: RERUN_LLM_CHUNK_SIZE,
      rows: [],
      isLoading: true,
      errorMessage: '',
      showAllSubjects: false,
      llmModalRow: null,
      previewLineCount: 5,
      rerunBusyId: null,
      rerunBatchBusy: false,
      rerunError: '',
      inboxCandidatesByRowId: {},
      candidatesError: '',
      inboxActionBusyKey: null,
      INBOX_GROUND_TRUTH_INTENTS,
      calibrationRows: [],
      calibrationLoading: false,
      calibrationError: '',
      operatorEmail: '',
      groundTruthBusyRowId: null,
      undoAutoBusyRowId: null,
      updateSpreadsheet: readInboxUpdateSpreadsheetPref(),
    };
  },
  computed: {
    subjectPatternDisplay() {
      return BOOKING_REPLY_SUBJECT_ILIKE.replace('%', '…');
    },
    calibrationIntentColumns() {
      return ['confirm', 'decline', 'date_change_request'];
    },
    calibrationTableRows() {
      return buildCalibrationTableRows(this.calibrationRows, this.calibrationIntentColumns);
    },
    calibrationSummaryLine() {
      const { n, agree, pct } = calibrationOverallAgreement(this.calibrationRows);
      if (n === 0) {
        return 'No tagged rows yet. Mark truth on rows below (or use Confirm/Reject) to build the set.';
      }
      return `On ${n} tagged rows, the LLM agrees with truth on ${agree} (${pct}%).`;
    },
    calibrationBucketHint() {
      const hit = findHighestReliableBucketForIntent(
        this.calibrationRows,
        'confirm',
        CALIBRATION_MIN_ACCURACY,
        CALIBRATION_MIN_BUCKET_SAMPLE
      );
      if (!hit) {
        return `No confirm bucket yet meets ≥${CALIBRATION_MIN_ACCURACY * 100}% accuracy with n ≥ ${CALIBRATION_MIN_BUCKET_SAMPLE} (tune in inboxCalibration.js).`;
      }
      return `Highest bucket where confirm accuracy ≥ ${CALIBRATION_MIN_ACCURACY * 100}% with n ≥ ${CALIBRATION_MIN_BUCKET_SAMPLE}: confidence ≥ ${hit.lo} (${hit.n} rows, ${hit.agree} correct).`;
    },
  },
  async created() {
    await this.loadRows();
  },
  methods: {
    async loadRows() {
      this.isLoading = true;
      this.errorMessage = '';
      try {
        let q = supabase
          .from('email_inbox_log')
          .select('*')
          .order('received_at', { ascending: false, nullsFirst: false })
          .order('created_at', { ascending: false })
          .limit(300);

        if (!this.showAllSubjects) {
          q = q.ilike('subject', BOOKING_REPLY_SUBJECT_ILIKE);
        }

        const { data, error } = await q;
        if (error) throw error;
        this.rows = sortInboxRowsReverseChronological(data || []);
        await this.ensureOperatorEmail();
        await this.loadCandidatesForRows();
        await this.runAutoConfirmPass();
      } catch (err) {
        console.error('AdminInboxReviewPage loadRows', err);
        this.errorMessage = err.message || 'Failed to load inbox log.';
        this.rows = [];
        this.inboxCandidatesByRowId = {};
      } finally {
        this.isLoading = false;
      }
    },
    async ensureOperatorEmail() {
      if (this.operatorEmail) return;
      const {
        data: { session },
      } = await supabase.auth.getSession();
      this.operatorEmail = session?.user?.email || '';
    },
    async runAutoConfirmPass() {
      const setBy = this.operatorEmail || 'unknown@operator';
      const mergedIds = [];
      for (const row of this.rows) {
        const cands = this.inboxCandidatesByRowId[row.id] || [];
        const r = await attemptAutoConfirm(supabase, row, cands, { set_by: setBy });
        if (r.applied) {
          mergedIds.push(row.id);
          if (r.notifyPayload) {
            void notifyInboxAutoConfirmByEmail(supabase, r.notifyPayload);
          }
          if (this.updateSpreadsheet && r.confirmCand) {
            void syncLineupSheetFromInbox(supabase, {
              cand: r.confirmCand,
              action: 'confirm',
            });
          }
        }
      }
      if (mergedIds.length > 0) {
        await this.mergeUpdatedInboxRows(mergedIds);
      }
    },
    onUpdateSpreadsheetPrefChange() {
      saveInboxUpdateSpreadsheetPref(this.updateSpreadsheet);
    },
    async onCalibrationDetailsToggle(ev) {
      if (!ev.target.open) return;
      await this.loadCalibrationData();
    },
    async loadCalibrationData() {
      this.calibrationLoading = true;
      this.calibrationError = '';
      try {
        const { data, error } = await supabase
          .from('email_inbox_log')
          .select('parsed_intent, confidence, ground_truth_intent')
          .not('ground_truth_intent', 'is', null)
          .limit(5000);
        if (error) throw error;
        this.calibrationRows = data || [];
      } catch (err) {
        console.error('loadCalibrationData', err);
        this.calibrationError = err.message || 'Failed to load calibration.';
        this.calibrationRows = [];
      } finally {
        this.calibrationLoading = false;
      }
    },
    truthMatchesLlm(row) {
      if (!row.ground_truth_intent || row.parsed_intent == null) return false;
      return (
        String(row.ground_truth_intent).toLowerCase() ===
        String(row.parsed_intent).toLowerCase()
      );
    },
    truthDisagreesWithLlm(row) {
      return Boolean(
        row.ground_truth_intent &&
          row.parsed_intent != null &&
          !this.truthMatchesLlm(row)
      );
    },
    isUndoAutoBusy(rowId) {
      return this.undoAutoBusyRowId === rowId;
    },
    async onGroundTruthIntentChange(row, ev) {
      const raw = ev.target.value;
      const intent = raw === '' ? null : raw;
      await this.ensureOperatorEmail();
      const setBy = this.operatorEmail || '';
      this.groundTruthBusyRowId = row.id;
      try {
        const { error } = await setInboxRowGroundTruth(supabase, row.id, {
          intent,
          application_ids: intent == null ? null : row.ground_truth_application_ids ?? null,
          set_by: setBy,
        });
        if (error) throw error;
        row.ground_truth_intent = intent;
        if (intent == null) {
          row.ground_truth_application_ids = null;
        }
        row.ground_truth_set_at = new Date().toISOString();
        row.ground_truth_set_by = setBy;
      } catch (err) {
        console.error('onGroundTruthIntentChange', err);
        Swal.fire('Error', err.message || 'Failed to save truth', 'error');
      } finally {
        this.groundTruthBusyRowId = null;
      }
    },
    async undoAutoConfirmForRow(row) {
      const applicationId = row.ground_truth_application_ids?.[0];
      if (!applicationId) {
        Swal.fire('Error', 'No application id on this row to undo.', 'error');
        return;
      }
      const cands = this.inboxCandidatesByRowId[row.id] || [];
      const cand = cands.find((c) => c.id === applicationId);
      if (!cand) {
        Swal.fire('Error', 'Could not find application; refresh and try again.', 'error');
        return;
      }
      const detail = `${cand.comedian_name} — ${this.formatInboxEventLine(cand)}`;
      const { isConfirmed } = await Swal.fire({
        title: 'Undo auto-confirm?',
        text: `Revert application to Offered and restore same-month exclusivity rejects. ${detail}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Undo',
        cancelButtonText: 'Cancel',
      });
      if (!isConfirmed) return;

      this.undoAutoBusyRowId = row.id;
      try {
        await undoAutoConfirmInbox(supabase, row, cand, applicationId);
        await this.mergeUpdatedInboxRows([row.id]);
        Swal.fire('Undone', 'Application set back to Offered; cascade restored where applicable.', 'success');
      } catch (err) {
        console.error('undoAutoConfirmForRow', err);
        Swal.fire('Error', err.message || 'Failed to undo', 'error');
      } finally {
        this.undoAutoBusyRowId = null;
      }
    },
    normalizedInboxSender(row) {
      return normalizeInboxSender(row);
    },
    offerDateYmd(row) {
      return resolveOfferDateForInboxRow(row);
    },
    formatOfferDateYmd(ymd) {
      if (!ymd) return '';
      const [y, m, d] = ymd.split('-').map(Number);
      const dt = new Date(Date.UTC(y, m - 1, d));
      const label = dt.toLocaleDateString('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
      return `${label} (${ymd})`;
    },
    formatEffectiveOfferDate(row) {
      return this.formatOfferDateYmd(resolveOfferDateForInboxRow(row));
    },
    formatSubjectOfferDate(row) {
      return this.formatOfferDateYmd(parseOfferEventDateFromSubject(row?.subject || ''));
    },
    subjectOfferDiffersFromEffective(row) {
      const inferred = row?.llm_inferred_event_date;
      const subject = parseOfferEventDateFromSubject(row?.subject || '');
      const effective = resolveOfferDateForInboxRow(row);
      if (!inferred || !subject) return false;
      return effective !== subject;
    },
    /** @deprecated use formatEffectiveOfferDate */
    formatParsedOfferDate(row) {
      return this.formatEffectiveOfferDate(row);
    },
    async loadCandidatesForRows() {
      this.candidatesError = '';
      const rows = this.rows || [];
      if (rows.length === 0) {
        this.inboxCandidatesByRowId = {};
        return;
      }
      try {
        const idSet = new Set();
        rows.forEach((r) => (r.application_ids || []).forEach((id) => idSet.add(id)));
        const ids = [...idSet];

        const emails = [
          ...new Set(rows.map((r) => normalizeInboxSender(r)).filter(Boolean)),
        ];

        const appsById = new Map();
        if (ids.length > 0) {
          const { data, error } = await supabase
            .from('applications')
            .select(INBOX_APP_SELECT)
            .in('id', ids);
          if (error) throw error;
          (data || []).forEach((a) => appsById.set(a.id, a));
        }

        const allAppsByEmail = new Map();
        await Promise.all(
          emails.map(async (email) => {
            const { data, error } = await supabase
              .from('applications')
              .select(INBOX_APP_SELECT)
              .ilike('email', email);
            if (error) throw error;
            allAppsByEmail.set(email.toLowerCase(), data || []);
          })
        );

        const nextMap = {};
        for (const row of rows) {
          nextMap[row.id] = buildInboxCandidatesMapForRow(row, appsById, allAppsByEmail);
        }
        this.inboxCandidatesByRowId = nextMap;
      } catch (err) {
        console.error('loadCandidatesForRows', err);
        this.candidatesError = err.message || 'Failed to load applications for inbox rows.';
        this.inboxCandidatesByRowId = {};
      }
    },
    formatInboxEventLine(cand) {
      const ev = cand.event;
      if (!ev) return '—';
      const d = ev.event_date
        ? new Date(ev.event_date).toLocaleDateString('en-GB', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })
        : '—';
      const venue = ev.venue_name || '';
      const name = ev.event_name || '';
      return [d, name, venue].filter(Boolean).join(' · ');
    },
    getStatusLabel,
    getStatusColor,
    isTerminalInboxStatus(status) {
      return (
        status === APPLICATION_STATUSES.CONFIRMED || status === APPLICATION_STATUSES.REJECTED
      );
    },
    canConfirmInboxApplication(cand) {
      return cand.status === APPLICATION_STATUSES.OFFERED;
    },
    canRejectInboxApplication(cand) {
      if (cand.status === APPLICATION_STATUSES.REJECTED) return false;
      if (cand.status === APPLICATION_STATUSES.CONFIRMED) return false;
      if (cand.status === APPLICATION_STATUSES.OFFERED) return true;
      return (
        cand.status === APPLICATION_STATUSES.PENDING && Boolean(cand.matchesEmailOfferDate)
      );
    },
    isInboxActionBusy(rowId, appId) {
      return this.inboxActionBusyKey === `${rowId}:${appId}`;
    },
    async confirmInboxApplication(row, cand) {
      if (cand.status !== APPLICATION_STATUSES.OFFERED) return;
      const detail = `${cand.comedian_name} — ${this.formatInboxEventLine(cand)}`;
      const { isConfirmed } = await Swal.fire({
        title: 'Confirm booking?',
        text: `Set application to Confirmed. ${detail}`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Confirm',
        cancelButtonText: 'Cancel',
      });
      if (!isConfirmed) return;

      const key = `${row.id}:${cand.id}`;
      this.inboxActionBusyKey = key;
      try {
        await this.ensureOperatorEmail();
        await confirmApplicationFromInboxCandidate(supabase, cand);
        await setInboxRowGroundTruthIfUnset(supabase, row.id, {
          intent: 'confirm',
          application_ids: [cand.id],
          set_by: this.operatorEmail || '',
        });

        cand.status = APPLICATION_STATUSES.CONFIRMED;
        let sheetNote = null;
        if (this.updateSpreadsheet) {
          const sheetResult = await syncLineupSheetFromInbox(supabase, {
            cand,
            action: 'confirm',
          });
          sheetNote = formatSheetSyncResultNote(sheetResult, { enabled: this.updateSpreadsheet });
        }
        Swal.fire(
          'Updated',
          [
            'Application confirmed (same-month exclusivity applied like on Events page).',
            sheetNote,
          ]
            .filter(Boolean)
            .join(' '),
          'success'
        );
      } catch (err) {
        console.error('confirmInboxApplication', err);
        Swal.fire('Error', err.message || 'Failed to confirm application', 'error');
      } finally {
        this.inboxActionBusyKey = null;
      }
    },
    async rejectInboxApplication(row, cand) {
      if (!this.canRejectInboxApplication(cand)) return;
      const detail = `${cand.comedian_name} — ${this.formatInboxEventLine(cand)}`;
      const { isConfirmed } = await Swal.fire({
        title: 'Reject application?',
        text: `Set application to Rejected. ${detail}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Reject',
        cancelButtonText: 'Cancel',
      });
      if (!isConfirmed) return;

      const key = `${row.id}:${cand.id}`;
      this.inboxActionBusyKey = key;
      try {
        await this.ensureOperatorEmail();
        const { error } = await supabase
          .from('applications')
          .update({ status: APPLICATION_STATUSES.REJECTED })
          .eq('id', cand.id);
        if (error) throw error;
        await setInboxRowGroundTruthIfUnset(supabase, row.id, {
          intent: 'decline',
          application_ids: [cand.id],
          set_by: this.operatorEmail || '',
        });
        cand.status = APPLICATION_STATUSES.REJECTED;
        let sheetNote = null;
        if (this.updateSpreadsheet) {
          const sheetResult = await syncLineupSheetFromInbox(supabase, {
            cand,
            action: 'reject',
          });
          sheetNote = formatSheetSyncResultNote(sheetResult, { enabled: this.updateSpreadsheet });
        }
        Swal.fire(
          'Updated',
          ['Application rejected.', sheetNote].filter(Boolean).join(' '),
          'success'
        );
      } catch (err) {
        console.error('rejectInboxApplication', err);
        Swal.fire('Error', err.message || 'Failed to reject application', 'error');
      } finally {
        this.inboxActionBusyKey = null;
      }
    },
    /** After LLM re-run: patch rows in place so we do not toggle isLoading (which hides the list and jumps scroll). */
    async mergeUpdatedInboxRows(ids) {
      const unique = [...new Set((ids || []).filter(Boolean))];
      if (unique.length === 0) return;
      const { data, error } = await supabase.from('email_inbox_log').select('*').in('id', unique);
      if (error) throw error;
      const byId = new Map((data || []).map((r) => [r.id, r]));
      this.rows = sortInboxRowsReverseChronological(
        this.rows.map((r) => (byId.has(r.id) ? byId.get(r.id) : r))
      );
      if (this.llmModalRow && byId.has(this.llmModalRow.id)) {
        this.llmModalRow = byId.get(this.llmModalRow.id);
      }
      await this.loadCandidatesForRows();
    },
    formatWhen(row) {
      const t = row.received_at || row.created_at;
      if (!t) return '—';
      return new Date(t).toLocaleString('en-GB', {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
    },
    formatConfidence(n) {
      const x = Number(n);
      if (Number.isNaN(x)) return '';
      return `${Math.round(x * 100)}% conf.`;
    },
    intentClass(intent) {
      if (!intent) return '';
      const i = String(intent).toLowerCase();
      if (i === 'confirm') return 'intent-confirm';
      if (i === 'decline') return 'intent-decline';
      if (i === 'unknown') return 'intent-unknown';
      return 'intent-other';
    },
    bodyLines(text) {
      if (!text || typeof text !== 'string') return [];
      return text.split(/\r?\n/);
    },
    bodyPreview(text) {
      const lines = this.bodyLines(text);
      if (lines.length === 0) return '—';
      return lines.slice(0, this.previewLineCount).join('\n');
    },
    bodyHasMoreLines(text) {
      return this.bodyLines(text).length > this.previewLineCount;
    },
    sourceLabel(row) {
      const s = row.classification_source;
      if (s === 'pending_llm') return 'LLM running (refresh in a moment)';
      if (s === 'llm') return 'via LLM';
      if (s === 'llm_parse_failed') return 'LLM response not parseable';
      if (s === 'llm_fallback_regex') return 'LLM failed → regex (legacy)';
      if (s === 'regex') return 'regex only (legacy)';
      if (row.llm_summary) return 'via LLM (legacy row)';
      return '';
    },
    hasLlmDebug(row) {
      return Boolean(row.llm_prompt || row.llm_raw_response);
    },
    openLlmModal(row) {
      this.llmModalRow = row;
    },
    closeLlmModal() {
      this.llmModalRow = null;
    },
    formatRerunLlmError(err) {
      const base = err?.message || 'Re-run LLM failed.';
      if (/Failed to send|FunctionsFetchError|NetworkError|502/i.test(base)) {
        return `${base} Often this is an Edge Function timeout (batch too heavy) or a bad deploy — use single-row re-run or a smaller batch; see docs/DESIGN_HALF_CLAW_EMAIL_PIPELINE.md (stream C troubleshooting).`;
      }
      return base;
    },
    async ensureAdminSession() {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Sign in again to re-run LLM.');
      }
      return session;
    },
    async invokeRerunLlm(ids) {
      const session = await this.ensureAdminSession();
      const { data, error } = await supabase.functions.invoke('email-inbox-rerun-llm', {
        body: { ids },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      if (error) throw error;
      return data;
    },
    async rerunLlmSingle(row) {
      if (
        !window.confirm(
          'Re-run LLM on this row? This calls OpenRouter and overwrites classification and LLM debug fields.'
        )
      ) {
        return;
      }
      this.rerunError = '';
      this.rerunBusyId = row.id;
      try {
        const data = await this.invokeRerunLlm([row.id]);
        const r = data?.results?.[0];
        if (!r?.success) {
          throw new Error(r?.error || 'Re-run failed');
        }
        await this.mergeUpdatedInboxRows([row.id]);
      } catch (err) {
        console.error('rerunLlmSingle', err);
        this.rerunError = this.formatRerunLlmError(err);
      } finally {
        this.rerunBusyId = null;
      }
    },
    chunkIds(list, size) {
      const out = [];
      for (let i = 0; i < list.length; i += size) {
        out.push(list.slice(i, i + size));
      }
      return out;
    },
    async rerunLlmMissingDebugBatch() {
      const missing = this.rows.filter((r) => !r.llm_prompt || !r.llm_raw_response).slice(0, 20);
      if (missing.length === 0) {
        this.rerunError = 'No loaded rows are missing LLM prompt or raw response.';
        return;
      }
      const nReq = Math.ceil(missing.length / RERUN_LLM_CHUNK_SIZE);
      if (
        !window.confirm(
          `Re-run LLM on up to ${missing.length} row(s) missing prompt/raw? This will call OpenRouter (${nReq} Edge request(s), max ${RERUN_LLM_CHUNK_SIZE} ids each; may take a few minutes).`
        )
      ) {
        return;
      }
      this.rerunError = '';
      this.rerunBatchBusy = true;
      const processedIds = missing.map((r) => r.id);
      try {
        const batches = this.chunkIds(processedIds, RERUN_LLM_CHUNK_SIZE);
        for (let i = 0; i < batches.length; i++) {
          const ids = batches[i];
          const data = await this.invokeRerunLlm(ids);
          const failed = (data?.results || []).filter((x) => !x.success);
          if (failed.length > 0) {
            throw new Error(failed.map((f) => `${f.id}: ${f.error || 'failed'}`).join('; '));
          }
          if (i < batches.length - 1) {
            await new Promise((r) => setTimeout(r, 750));
          }
        }
        await this.mergeUpdatedInboxRows(processedIds);
      } catch (err) {
        console.error('rerunLlmMissingDebugBatch', err);
        this.rerunError = this.formatRerunLlmError(err);
      } finally {
        this.rerunBatchBusy = false;
      }
    },
    async logout() {
      await logoutAdmin();
      this.$router.push('/');
    },
  },
};
</script>

<style scoped>
.inbox-section {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 16px 48px;
}

.filter-hint {
  font-size: 0.95rem;
  color: #444;
  margin-bottom: 1rem;
  line-height: 1.5;
}

.filter-hint code {
  background: #f0f4f8;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.9em;
}

.toggle-all {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-left: 1rem;
  cursor: pointer;
  font-weight: 500;
}

.batch-rerun-wrap {
  display: block;
  margin-top: 0.75rem;
}

.batch-rerun-hint {
  display: block;
  margin-top: 0.35rem;
  font-size: 0.8rem;
  color: #6c757d;
}

.rerun-llm-batch-btn {
  padding: 0.4rem 0.75rem;
  font-size: 0.85rem;
  background: #fff;
  border: 1px solid #1864ab;
  color: #1864ab;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
}

.rerun-llm-batch-btn:hover:not(:disabled) {
  background: #e7f5ff;
}

.rerun-llm-batch-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.rerun-error-banner {
  margin: 0 0 1rem;
  padding: 0.65rem 1rem;
  border-radius: 8px;
  background: #fff4e6;
  color: #8a4b00;
  font-size: 0.9rem;
}

.rerun-row-actions {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.35rem;
}

.rerun-llm-btn {
  padding: 0.35rem 0.65rem;
  font-size: 0.8rem;
  background: #1864ab;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
}

.rerun-llm-btn:hover:not(:disabled) {
  background: #145a9e;
}

.rerun-llm-btn:disabled {
  opacity: 0.65;
  cursor: wait;
}

.loading,
.empty-state,
.error-banner {
  padding: 1.5rem;
  border-radius: 8px;
}

.loading {
  background: #f8f9fa;
}

.empty-state {
  background: #f8f9fa;
  color: #555;
}

.error-banner {
  background: #fde8e8;
  color: #8b1a1a;
}

.inbox-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.inbox-card {
  border: 1px solid #dee2e6;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.card-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.5rem 1rem;
  padding: 0.65rem 1rem;
  background: #f1f3f5;
  border-bottom: 1px solid #e9ecef;
  font-size: 0.88rem;
}

.meta-time {
  font-weight: 600;
  color: #212529;
  flex-shrink: 0;
}

.meta-subject {
  flex: 1 1 200px;
  color: #495057;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.meta-sender {
  color: #6c757d;
  flex-shrink: 0;
}

.card-body-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 0;
  align-items: start;
}

@media (max-width: 900px) {
  .card-body-grid {
    grid-template-columns: 1fr;
  }
}

.body-panel {
  padding: 0.65rem 0.85rem;
  border-right: 1px solid #e9ecef;
  min-height: 0;
  text-align: left;
  max-width: 100%;
}

@media (max-width: 900px) {
  .body-panel {
    border-right: none;
    border-bottom: 1px solid #e9ecef;
  }
}

.classification-panel {
  padding: 0.65rem 0.85rem;
  background: #fafbfc;
  text-align: left;
  min-width: 0;
}

.classify-summary-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem 1rem;
  align-items: start;
}

.classify-summary-grid--single {
  grid-template-columns: 1fr;
}

.classify-column,
.summary-column {
  min-width: 0;
}

.summary-column .llm-summary {
  margin-top: 0.2rem;
}

@media (max-width: 640px) {
  .classify-summary-grid {
    grid-template-columns: 1fr;
  }
}

.panel-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #868e96;
  margin-bottom: 0.35rem;
  font-weight: 600;
}

.panel-label.subtle {
  margin-top: 0.75rem;
  margin-bottom: 0.25rem;
}

.body-text {
  margin: 0;
  font-family: ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  font-size: 1rem;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
  color: #212529;
  text-align: left;
}

.body-preview {
  max-height: 5.25em;
  overflow: hidden;
}

.body-details {
  margin-top: 0.5rem;
  text-align: left;
}

.body-details summary {
  cursor: pointer;
  font-size: 0.85rem;
  color: #1864ab;
  user-select: none;
}

.body-full {
  margin-top: 0.5rem;
  max-height: 24rem;
  overflow: auto;
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.source-row {
  margin-top: 0.35rem;
}

.source-badge {
  font-size: 0.75rem;
  color: #495057;
  background: #e9ecef;
  padding: 0.15rem 0.45rem;
  border-radius: 4px;
}

.llm-debug-btn {
  display: block;
  margin-top: 0.6rem;
  padding: 0.35rem 0.65rem;
  font-size: 0.8rem;
  background: #fff;
  border: 1px solid #ced4da;
  border-radius: 6px;
  cursor: pointer;
  color: #1864ab;
}

.llm-debug-btn:hover {
  background: #f1f3f5;
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 2rem 1rem;
  overflow-y: auto;
}

.modal-dialog {
  background: #fff;
  border-radius: 10px;
  max-width: 900px;
  width: 100%;
  max-height: calc(100vh - 4rem);
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #dee2e6;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.1rem;
}

.modal-close {
  border: none;
  background: transparent;
  font-size: 1.5rem;
  line-height: 1;
  cursor: pointer;
  color: #495057;
  padding: 0 0.25rem;
}

.modal-body-scroll {
  overflow-y: auto;
  padding: 1rem;
  text-align: left;
}

.modal-block {
  margin-bottom: 1.25rem;
}

.modal-block:last-child {
  margin-bottom: 0;
}

.modal-block-title {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #868e96;
  font-weight: 600;
  margin-bottom: 0.35rem;
}

.modal-pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.8rem;
  line-height: 1.45;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 0.75rem;
  max-height: 40vh;
  overflow: auto;
}

.intent-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
}

.intent-badge {
  display: inline-block;
  padding: 0.25rem 0.6rem;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.85rem;
  text-transform: lowercase;
}

.intent-confirm {
  background: #d3f9d8;
  color: #2b8a3e;
}

.intent-decline {
  background: #ffe3e3;
  color: #c92a2a;
}

.intent-unknown {
  background: #e9ecef;
  color: #495057;
}

.intent-other {
  background: #e7f5ff;
  color: #1864ab;
}

.confidence {
  font-size: 0.85rem;
  color: #495057;
}

.llm-summary {
  margin: 0;
  font-size: 0.82rem;
  line-height: 1.38;
  color: #343a40;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.app-ids {
  font-size: 0.85rem;
  font-family: ui-monospace, monospace;
  color: #495057;
}

.offer-date-block {
  margin-top: 0.4rem;
  padding: 0.35rem 0.5rem;
  background: #e7f5ff;
  border-radius: 6px;
  border: 1px solid #a5d8ff;
}

.offer-date-value {
  margin: 0;
  font-size: 0.8rem;
  font-weight: 600;
  color: #1864ab;
}

.offer-date-source {
  font-weight: 500;
  color: #364fc7;
}

.offer-date-subject-hint {
  margin: 0.25rem 0 0;
  font-size: 0.75rem;
  color: #495057;
}

.inbox-apps-panel {
  margin-top: 0.35rem;
}

.candidates-error {
  margin: 0.35rem 0 0;
  font-size: 0.85rem;
  color: #c92a2a;
}

.candidates-empty {
  margin: 0.35rem 0 0;
  font-size: 0.85rem;
  color: #495057;
  line-height: 1.45;
}

.inbox-app-list {
  list-style: none;
  margin: 0.35rem 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.inbox-app-row {
  padding: 0.4rem 0.5rem;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: #fff;
}

.inbox-app-row.row-date-match {
  border-color: #74c0fc;
  background: #f8fbff;
}

.inbox-app-row.row-llm-only {
  border-style: dashed;
}

.inbox-app-primary {
  margin-bottom: 0.25rem;
}

.inbox-app-name {
  display: block;
  font-size: 0.86rem;
  margin-bottom: 0.1rem;
  line-height: 1.25;
}

.inbox-app-event {
  display: block;
  font-size: 0.74rem;
  line-height: 1.35;
  color: #495057;
  word-break: break-word;
}

.inbox-app-badges-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.25rem 0.45rem;
}

.inbox-app-badges-actions--terminal {
  justify-content: flex-start;
}

.inbox-candidate-badges {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem 0.35rem;
  margin-bottom: 0;
  flex: 1 1 auto;
  min-width: 0;
}

.status-chip {
  display: inline-block;
  padding: 0.08rem 0.35rem;
  border: 1px solid;
  border-radius: 3px;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: capitalize;
  background: #fff;
}

.date-match-badge {
  font-size: 0.65rem;
  font-weight: 600;
  padding: 0.08rem 0.35rem;
  border-radius: 3px;
  background: #d0ebff;
  color: #1864ab;
}

.llm-suggest-badge {
  font-size: 0.65rem;
  font-weight: 600;
  padding: 0.08rem 0.35rem;
  border-radius: 3px;
  background: #e5dbff;
  color: #5f3dc4;
}

.date-mismatch-badge {
  font-size: 0.65rem;
  font-weight: 600;
  padding: 0.08rem 0.35rem;
  border-radius: 3px;
  background: #fff4e6;
  color: #d9480f;
}

.cand-warn {
  margin: 0 0 0.25rem;
  font-size: 0.7rem;
  line-height: 1.3;
  color: #495057;
}

.cand-warn-link {
  margin-left: 0.15rem;
}

.done-hint {
  font-size: 0.7rem;
  color: #868e96;
  margin: 0 0 0.15rem;
}

.inbox-app-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  align-items: center;
}

.inbox-confirm-btn,
.inbox-reject-btn {
  padding: 0.22rem 0.5rem;
  font-size: 0.72rem;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 600;
  border: none;
}

.inbox-confirm-btn {
  background: #2b8a3e;
  color: #fff;
}

.inbox-confirm-btn:hover:not(:disabled) {
  background: #237032;
}

.inbox-reject-btn {
  background: #fff;
  color: #c92a2a;
  border: 1px solid #ffc9c9;
}

.inbox-reject-btn:hover:not(:disabled) {
  background: #fff5f5;
}

.inbox-confirm-btn:disabled,
.inbox-reject-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.admin-container {
  min-height: 100vh;
  background: #f8f9fa;
}

.admin-header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: white;
  border-bottom: 1px solid #dee2e6;
}

.admin-header h2 {
  margin: 0;
  font-size: 1.35rem;
}

.admin-nav {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.nav-button {
  padding: 8px 14px;
  background: #007bff;
  color: white;
  text-decoration: none;
  border-radius: 6px;
  font-size: 0.9rem;
}

.nav-button:hover {
  background: #0056b3;
}

.logout-button {
  padding: 8px 14px;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
}

.logout-button:hover {
  background: #545b62;
}

.calibration-panel {
  margin: 0 auto 1rem;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  background: #fff;
  max-width: 1400px;
}

.calibration-summary {
  cursor: pointer;
  font-weight: 600;
  color: #1864ab;
  user-select: none;
}

.calibration-error {
  margin-top: 0.5rem;
  color: #c92a2a;
  font-size: 0.9rem;
}

.calibration-loading {
  margin-top: 0.5rem;
  color: #495057;
  font-size: 0.9rem;
}

.calibration-body {
  margin-top: 0.65rem;
}

.calibration-summary-line,
.calibration-hint {
  font-size: 0.88rem;
  line-height: 1.45;
  color: #343a40;
  margin: 0 0 0.5rem;
}

.calibration-hint {
  color: #1864ab;
  font-weight: 500;
}

.calibration-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.82rem;
}

.calibration-table th,
.calibration-table td {
  border: 1px solid #dee2e6;
  padding: 0.35rem 0.5rem;
  text-align: left;
}

.calibration-table th {
  background: #f1f3f5;
  font-weight: 600;
}

.calibration-footnote {
  margin: 0.65rem 0 0;
  font-size: 0.78rem;
  line-height: 1.45;
  color: #495057;
}

.truth-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem 0.5rem;
  margin-top: 0.5rem;
  font-size: 0.82rem;
}

.truth-label {
  font-weight: 600;
  color: #495057;
}

.truth-select {
  font-size: 0.82rem;
  padding: 0.2rem 0.35rem;
  border-radius: 4px;
  border: 1px solid #ced4da;
}

.truth-compare {
  font-size: 0.78rem;
  color: #495057;
}

.truth-mismatch-chip {
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.12rem 0.4rem;
  border-radius: 4px;
  background: #fff4e6;
  color: #d9480f;
}

.auto-confirmed-badge {
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.12rem 0.4rem;
  border-radius: 4px;
  background: #d3f9d8;
  color: #2b8a3e;
}

.undo-auto-row {
  margin-top: 0.35rem;
}

.undo-auto-btn {
  font-size: 0.72rem;
  padding: 0.2rem 0.45rem;
  border-radius: 4px;
  border: 1px solid #ced4da;
  background: #fff;
  color: #1864ab;
  cursor: pointer;
  font-weight: 600;
}

.undo-auto-btn:hover:not(:disabled) {
  background: #e7f5ff;
}

.undo-auto-btn:disabled {
  opacity: 0.55;
  cursor: wait;
}
</style>
